import React from 'react';
import CancelConfirmModal from './CancelConfirmModal';

function formatDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
}

function StorageBar({ used, limit, myUsage, isFamilyPool }) {
  if (!limit) return null;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const isWarning = pct >= 80;
  const isOver = pct >= 100;

  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8em', marginBottom: '0.3rem', opacity: 0.8 }}>
        <span>
          {isFamilyPool ? 'Family storage pool' : 'Storage'}
          {isFamilyPool && myUsage != null && myUsage > 0 && (
            <span style={{ opacity: 0.6, marginLeft: '0.5rem' }}>
              (your contributions: {formatBytes(myUsage)})
            </span>
          )}
        </span>
        <span style={{ color: isOver ? '#e53e3e' : isWarning ? '#c05621' : undefined }}>
          {formatBytes(used)} of {formatBytes(limit)} used
        </span>
      </div>
      <div style={{ height: '6px', borderRadius: '3px', background: '#e2e8f0', overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          width: `${pct}%`,
          borderRadius: '3px',
          background: isOver ? '#e53e3e' : isWarning ? '#ed8936' : 'var(--color-primary, #4a90d9)',
          transition: 'width 0.3s',
        }} />
      </div>
    </div>
  );
}

function StatusBadge({ label }) {
  const colors = {
    Free: { background: '#eee', color: '#555' },
    Personal: { background: '#dbeafe', color: '#1d4ed8' },
    Family: { background: '#d1fae5', color: '#065f46' },
  };
  const style = colors[label] || colors.Free;
  return (
    <span style={{
      ...style, padding: '2px 10px', borderRadius: '12px',
      fontSize: '0.8em', fontWeight: 'bold', marginLeft: '0.5rem',
    }}>
      {label}
    </span>
  );
}

function CurrentPlanStatus({ subscription, storageStatus, onSubscriptionChange }) {
  const [showCancel, setShowCancel] = React.useState(false);

  if (!subscription) return null;

  const {
    planName, subscriptionStatus, billingPeriod,
    periodEnd, grandfatheredUntil, gracePeriodEndsAt,
    allowsExport, allowsFamily,
  } = subscription;

  const isFamilyPool = storageStatus?.isFamilyPool;
  const poolUsed = storageStatus?.totalUsed;
  const poolLimit = storageStatus?.limit;
  const myUsage = storageStatus?.myUsage;

  const isGrandfathered = subscriptionStatus === 'grandfathered';
  const isGracePeriod = subscriptionStatus === 'grace_period';
  const isCancelled = subscription.cancelAtPeriodEnd;
  const isFree = planName === 'Free';

  return (
    <div style={{ marginBottom: '2rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
        <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Current Plan</span>
        <StatusBadge label={planName || 'Free'} />
      </div>

      {/* Grandfathered notice */}
      {isGrandfathered && grandfatheredUntil && (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '6px', padding: '0.75rem 1rem', marginBottom: '0.75rem', fontSize: '0.875em' }}>
          You're on a complimentary Personal plan through <strong>{formatDate(grandfatheredUntil)}</strong>.
          After that, your account will move to the Free tier. Your reflections will always be accessible.
        </div>
      )}

      {/* Grace period notice */}
      {isGracePeriod && gracePeriodEndsAt && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', padding: '0.75rem 1rem', marginBottom: '0.75rem', fontSize: '0.875em' }}>
          Your family plan has ended. You have until <strong>{formatDate(gracePeriodEndsAt)}</strong> to subscribe to keep creating new reflections.
          All your existing content remains safe.
        </div>
      )}

      {/* Cancellation notice */}
      {isCancelled && periodEnd && (
        <div style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: '6px', padding: '0.75rem 1rem', marginBottom: '0.75rem', fontSize: '0.875em' }}>
          Your subscription is cancelled and will end on <strong>{formatDate(periodEnd)}</strong>.
          You'll move to the Free tier after that date.
        </div>
      )}

      {/* Active subscription details */}
      {!isGrandfathered && !isGracePeriod && !isFree && (
        <div style={{ fontSize: '0.875em', opacity: 0.8, marginBottom: '0.5rem' }}>
          {billingPeriod === 'yearly' ? 'Annual' : 'Monthly'} billing
          {periodEnd && !isCancelled && <> · Renews {formatDate(periodEnd)}</>}
        </div>
      )}

      {/* Storage bar */}
      {poolLimit && (
        <StorageBar
          used={poolUsed}
          limit={poolLimit}
          myUsage={myUsage}
          isFamilyPool={isFamilyPool}
        />
      )}

      {/* Features summary */}
      <div style={{ fontSize: '0.85em', display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem', opacity: 0.8 }}>
        <span>{subscription.reflectionLimit ? `${subscription.reflectionCount} / ${subscription.reflectionLimit} reflections` : 'Unlimited reflections'}</span>
        {allowsExport && <span>Export enabled</span>}
        {allowsFamily && <span>Family archive</span>}
      </div>

      {/* Cancel link */}
      {!isFree && !isCancelled && !isGrandfathered && subscriptionStatus === 'active' && (
        <button
          onClick={() => setShowCancel(true)}
          style={{ background: 'none', border: 'none', color: '#e53e3e', fontSize: '0.85em', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
        >
          Cancel subscription
        </button>
      )}

      {showCancel && (
        <CancelConfirmModal
          subscription={subscription}
          onClose={() => setShowCancel(false)}
          onCancelled={(data) => {
            setShowCancel(false);
            onSubscriptionChange && onSubscriptionChange(data);
          }}
        />
      )}
    </div>
  );
}

export default CurrentPlanStatus;
